import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useAxios } from '../composables/axios';
import { AxiosError } from 'axios';
import Swal from 'sweetalert2';
import { useRouter } from 'vue-router';
import { AuthValidation } from '../validation/auth';
import { type RoleId, type RoleName } from '@/roles';
import { normalizeAuthPayload, type RawUserPayload, type User } from '@/services/authNormalizer';
import {
  setAuthState,
  clearAuthState,
  getAccessToken,
  getRefreshToken,
  AUTH_UPDATE_EVENT,
  type AuthUpdateDetail
} from '@/services/tokenStorage';
import type { PermissionCode } from '@/constants/permissions';

interface ApiResponseType<T> {
  data: T;
  status?: number;
  message?: string;
}

interface ErrorResponse {
  error?: string;
  message?: string;
}

interface TwoFactorChallenge {
  requires2fa: true;
  challengeId: string;
  otpExpiresAt?: string;
}

type LoginResult =
  | boolean
  | TwoFactorChallenge
  | { ok: false; status?: number; message?: string; retryAfterSeconds?: number };

interface VerifyOtpErrorPayload {
  success?: boolean;
  status?: number;
  message?: string;
  retryAfterSeconds?: number;
}

type VerifyOtpResult =
  | { ok: true }
  | { ok: false; status?: number; message?: string; retryAfterSeconds?: number };

interface ResendOtpResponse {
  requires2fa: true;
  challengeId: string;
  otpExpiresAt?: string;
}

type ResendOtpResult =
  | {
      ok: true;
      challengeId: string;
      otpExpiresAt?: string;
    }
  | { ok: false; status?: number; message?: string };

const isTwoFactorChallenge = (
  payload: RawUserPayload | TwoFactorChallenge
): payload is TwoFactorChallenge =>
  Boolean(
    (payload as TwoFactorChallenge).requires2fa === true &&
      typeof (payload as TwoFactorChallenge).challengeId === 'string'
  );

const platform = 'web';
const deviceId = '999';

export const useAuthStore = defineStore('auth', () => {
  const router = useRouter();
  const storedAccessToken = getAccessToken();
  const storedRefreshToken = getRefreshToken();
  const initialNormalized = normalizeAuthPayload(undefined, storedAccessToken, storedRefreshToken);

  const user = ref<User | null>(initialNormalized?.user ?? null);
  const token = ref<string | null>(initialNormalized?.accessToken ?? getAccessToken());
  const refreshTokenRef = ref<string | null>(initialNormalized?.refreshToken ?? storedRefreshToken);
  const { post, get } = useAxios();

  if (typeof window !== 'undefined') {
    window.addEventListener(AUTH_UPDATE_EVENT, (event: Event) => {
      const detail = (event as CustomEvent<AuthUpdateDetail>).detail;
      if (!detail) return;
      const normalized = normalizeAuthPayload(undefined, detail.accessToken, detail.refreshToken);
      token.value = normalized?.accessToken ?? null;
      refreshTokenRef.value = normalized?.refreshToken ?? null;
      user.value = normalized?.user ?? null;
    });
  }

  const isAuthenticated = computed(() => !!user.value);
  const currentRoleId = computed<RoleId | null>(() => user.value?.roleId ?? null);
  const currentRoleName = computed<RoleName | null>(
    () => user.value?.roleName ?? null
  );
  const currentPermissions = computed<PermissionCode[]>(() => user.value?.permissions ?? []);
  const permissionSet = computed(() => new Set(currentPermissions.value));

  const hasRole = (requiredRoles: RoleId | RoleId[]) => {
    if (!currentRoleId.value) return false;
    const roles = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];
    return roles.includes(currentRoleId.value);
  };

  const hasPermission = (permission: PermissionCode) => permissionSet.value.has(permission);
  const hasAnyPermission = (requiredPermissions: PermissionCode | PermissionCode[]) => {
    const permissions = Array.isArray(requiredPermissions)
      ? requiredPermissions
      : [requiredPermissions];
    return permissions.some(code => permissionSet.value.has(code));
  };
  const hasAllPermissions = (requiredPermissions: PermissionCode[]) =>
    requiredPermissions.every(code => permissionSet.value.has(code));

  const canEditActions = computed(() =>
    hasAnyPermission(['ACTION_CREATE', 'ACTION_UPDATE', 'ACTION_DELETE'])
  );
  const canViewOperations = computed(() => hasAnyPermission('OPERATION_VIEW'));
  const canManageProcesses = computed(() =>
    hasAnyPermission(['PROCESS_CREATE', 'PROCESS_UPDATE', 'PROCESS_DELETE'])
  );
  const canManageFrequencies = computed(() =>
    hasAnyPermission(['FREQUENCY_CREATE', 'FREQUENCY_UPDATE', 'FREQUENCY_DELETE'])
  );

  const login = async (email: string, password: string): Promise<LoginResult> => {
    const errors = AuthValidation.validateLoginForm(email, password);
    if (Object.keys(errors).length > 0) {
      await Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Girdiğiniz bilgileri kontrol edin.'
      });
      return false;
    }

    const params = { email, password, platform, deviceId };
    try {
      const response = await post<RawUserPayload | TwoFactorChallenge>(
        '/users/0.0.1/login/',
        params,
        { _skipAuthRefresh: true }
      );

      if (isTwoFactorChallenge(response)) {
        return response;
      }

      const normalized = normalizeAuthPayload(response as RawUserPayload);
      if (!normalized) {
        await Swal.fire({
          icon: 'error',
          title: 'Giriş başarısız',
          text: 'Kullanıcı adı veya şifre hatalı.'
        });
        return false;
      }

      user.value = normalized.user;
      token.value = normalized.accessToken;
      refreshTokenRef.value = normalized.refreshToken;

      setAuthState({
        accessToken: normalized.accessToken,
        refreshToken: normalized.refreshToken
      });

      fetchInitialData();
      return true;
    } catch (err) {
      const error = err as AxiosError<ErrorResponse & { retryAfterSeconds?: number }>;
      const status = error.response?.status;
      const retryAfterSeconds = error.response?.data?.retryAfterSeconds;
      const errorMessage =
        error.response?.data?.error || error.response?.data?.message || 'An unknown error occurred';
      console.error('Login failed:', errorMessage);
      return { ok: false, status, message: errorMessage, retryAfterSeconds };
    }
  };

  const verifyOtp = async (
    email: string,
    otp: string,
    challengeId: string
  ): Promise<VerifyOtpResult> => {
    const params = { email, otp, challengeId };
    try {
      const response = await post<RawUserPayload>(
        '/users/0.0.1/login/verify-otp',
        params,
        { _skipAuthRefresh: true }
      );

      const normalized = normalizeAuthPayload(response);
      if (!normalized) {
        return { ok: false };
      }

      user.value = normalized.user;
      token.value = normalized.accessToken;
      refreshTokenRef.value = normalized.refreshToken;

      setAuthState({
        accessToken: normalized.accessToken,
        refreshToken: normalized.refreshToken
      });

      fetchInitialData();
      return { ok: true };
    } catch (err) {
      const error = err as AxiosError<VerifyOtpErrorPayload & ErrorResponse>;
      const status = error.response?.status;
      const message = error.response?.data?.message || error.response?.data?.error;
      const retryAfterSeconds = error.response?.data?.retryAfterSeconds;
      const errorMessage =
        message || 'An unknown error occurred';
      console.error('OTP verification failed:', errorMessage);
      return { ok: false, status, message: errorMessage, retryAfterSeconds };
    }
  };

  const resendOtp = async (
    email: string,
    challengeId: string
  ): Promise<ResendOtpResult> => {
    const params = { email, challengeId, platform, deviceId };
    try {
      const response = await post<ResendOtpResponse>(
        '/users/0.0.1/login/resend-otp',
        params,
        { _skipAuthRefresh: true }
      );

      return {
        ok: true,
        challengeId: response.challengeId,
        otpExpiresAt: response.otpExpiresAt
      };
    } catch (err) {
      const error = err as AxiosError<ErrorResponse>;
      const status = error.response?.status;
      const message = error.response?.data?.message || error.response?.data?.error;
      const errorMessage = message || 'An unknown error occurred';
      console.error('OTP resend failed:', errorMessage);
      return { ok: false, status, message: errorMessage };
    }
  };

  const fetchInitialData = async () => {
    try {
      const response: any = await get('/preferences/0.0.1', {
        params: {
          type: 'settings',
          name: 'isSetup'
        },
        headers: {
          Authorization: `Bearer ${token.value}`
        }
      });

      if (response) {
        if (response[0]?.value === 'true') {
          router.push('/Promotional');
        } else {
          router.push('/main');
        }
      }
    } catch (error) {
      await Swal.fire({
        icon: 'error',
        title: 'Giriş Başarısız',
        text: 'Tercihleri alırken bir hata oluştu.'
      });
    }
  };

  const signUp = async (email: string, password: string, phone: string, fullname: string) => {
    const errors = AuthValidation.validateSignUpForm(email, password, phone, fullname);
    if (Object.keys(errors).length > 0) {
      await Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Girdiğiniz bilgileri kontrol edin.'
      });
      return false;
    }

    const referenceCode = 'TEST';
    const params = {
      email,
      password,
      phone,
      fullname,
      referenceCode,
      platform,
      deviceId
    };

    try {
      const response = await post<RawUserPayload>('/users/0.0.1/register', params);
      const normalized = normalizeAuthPayload(response);

      if (!normalized) {
        await Swal.fire({
          icon: 'error',
          title: 'Kayıt işlemi başarısız',
          text: 'Girilen bilgiler geçersiz. Lütfen kontrol edin.'
        });
        return false;
      }

      user.value = normalized.user;
      token.value = normalized.accessToken;
      refreshTokenRef.value = normalized.refreshToken;

      setAuthState({
        accessToken: normalized.accessToken,
        refreshToken: normalized.refreshToken
      });

      return true;
    } catch (err) {
      const error = err as AxiosError<ErrorResponse>;
      const errorMessage =
        error.response?.data?.error || error.response?.data?.message || 'An unknown error occurred';
      await Swal.fire({
        icon: 'error',
        title: 'Kayıt Başarısız',
        text: 'İşlem başarısız: ' + errorMessage
      });
      return false;
    }
  };

  const forgotPassword = async (email: string) => {
    const newParams = { email: email, deviceId: deviceId, platform: platform };
    try {
      await post<ApiResponseType<any>>('/users/0.0.1/request', newParams);
      await Swal.fire({
        icon: 'info',
        title: 'Şifre Sıfırlama',
        text: `Eğer ${email} adresine sahip bir hesabımız varsa, kısa süre içinde bir şifre sıfırlama bağlantısı alacaksınız.`
      });
      return true;
    } catch (err) {
      await Swal.fire({
        icon: 'error',
          title: 'Şifre Sıfırlama İsteği Başarısız',
          text: 'Lütfen tekrar deneyiniz'
      });
      return false;
    }
  };

  const forgotPasswordApply = async (code: string | undefined, password: string) => {
    try {
      await post<ApiResponseType<any>>('/users/0.0.1/request/save', {
        password,
        code
      });

      await Swal.fire({
        icon: 'success',
        title: 'Şifre Sıfırlama Uygulama Başarılı',
        text: 'Şifreniz başarıyla değiştirildi.',
        confirmButtonText: 'Tamam'
      });

      router.push('/');
      return true;
    } catch (err) {
      await Swal.fire({
        icon: 'error',
        title: 'Şifre Sıfırlama Uygulama Başarısız',
        text: 'Şifre sıfırlama işlemi başarısız'
      });
      return false;
    }
  };

  const checkAuth = async () => {
    const currentAccessToken = getAccessToken();
    const storedRefresh = getRefreshToken();
    const normalized = normalizeAuthPayload(undefined, currentAccessToken, storedRefresh);

    if (!normalized) {
      clearAuthState();
      token.value = null;
      user.value = null;
      refreshTokenRef.value = null;
      return;
    }

    user.value = normalized.user;
    token.value = normalized.accessToken;
    refreshTokenRef.value = normalized.refreshToken;

    setAuthState(
      {
        accessToken: normalized.accessToken,
        refreshToken: normalized.refreshToken,
        notify: false
      }
    );
  };

  return {
    user,
    token,
    refreshToken: refreshTokenRef,
    isAuthenticated,
    roleId: currentRoleId,
    roleName: currentRoleName,
    permissions: currentPermissions,
    hasRole,
    canEditActions,
    canViewOperations,
    canManageProcesses,
    canManageFrequencies,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    login,
    verifyOtp,
    resendOtp,
    checkAuth,
    forgotPassword,
    forgotPasswordApply,
    signUp
  };
});
